import { useCallback, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { getCookie } from 'cookies-next';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import { useAccount } from '@/frontastic';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { getCookieValue, getSiteDivisionCode, createUrl } from '@/helpers/utils/siteUtils';
import { AddDivisionPayload, CreateCustomerPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import {
  COOKIE_KEYS,
  CUSTOMER_DETAILS_PAGE_PATH,
  FORM_FIELD_SPECS,
  SITE_KEY_MAP_MO,
} from '../../../../../constants/constant';
import { DEFAULT_COUNTRY, RESPONSE_SUCCESS_MESSAGE } from '../../customer-detail/constants';
import { CustomerSearchInputRecord } from '../../search/types';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';
import { useToast } from '../../ui/use-toast';
const AddUserToDivisionDialog = ({
  Texts,
  allRowsData,
  dialogTriggerText,
  dialogDescriptionText,
  divisionToAdd,
  customerId,
  accountNumber,
  setAllRowsDataFuncRef,
  shouldAddCustomerMaster = false,
  customerMasterData,
}: {
  Texts: any;
  allRowsData: any;
  setAllRowsDataFuncRef: any;
  dialogTriggerText?: string;
  dialogDescriptionText: string;
  divisionToAdd?: string;
  customerId: string;
  accountNumber: string;
  shouldAddCustomerMaster?: boolean;
  customerMasterData?: CustomerSearchInputRecord;
}) => {
  const router = useRouter();
  const mailingRecordId = useSearchParams().get('mailingRecordId') ?? '';
  const [show, setShow] = useState(false);
  const { addDivision, createCustomer } = useAccount();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const params = useSearchParams();

  const addCustomerMaster = async () => {
    const payload = {
      query: {
        X_IncludeSuggestedCustomers: false,
        X_MailingRecordId:
          customerMasterData?.DivisionCustomers?.[0]?.CurrentPromotionInfo?.BestMailingRecord?.mailingRecordId ?? '',
        X_INITATED_ON: '',
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
      data: {
        CustomerInfo: customerMasterData?.CustomerInfo,
        BillingAddress: {
          LocationInfo: { ...customerMasterData?.BillingAddress?.LocationInfo, country: DEFAULT_COUNTRY },
        },
        DivisionCustomers: customerMasterData?.DivisionCustomers,
      },
    } as CreateCustomerPayload;
    setShow(false);
    setLoading(true);
    createCustomer(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        const divisionCode = getCookie(COOKIE_KEYS.selectedSite);
        const CDP_URL = createUrl(
          response?.Customer?.customerId,
          response?.Customer?.CustomerInfo?.omsCustomerId,
          getSiteDivisionCode(divisionCode ?? ''),
        );
        router.push(`/customer-details/${CDP_URL}`);
      } else {
        setLoading(false);
        ErrorNotification(toast, {
          description: Texts.errorWhileCreating,
          action: <ErrorIcon className="h-5 w-5" />,
        });
      }
    });
  };
  const handleAddDivision = useCallback(async () => {
    if (shouldAddCustomerMaster) {
      addCustomerMaster();
      return;
    }
    const payload = {
      query: {
        customerId: customerId,
        X_MAILING_RECORD_ID:
          customerMasterData?.DivisionCustomers?.[0]?.CurrentPromotionInfo?.BestMailingRecord?.mailingRecordId ??
          params?.get(FORM_FIELD_SPECS?.catalogId?.id) ??
          '',
      },
      data: {
        divisionCode: divisionToAdd,
      },
    } as AddDivisionPayload;
    const updatedallRowsdata = allRowsData?.map((item: any) => {
      if (item?.accountNumber === customerId) {
        item?.divisions?.divisionsData?.unshift(divisionToAdd);
      }
      return item;
    });
    setShow(false);
    setLoading(true);
    await addDivision(payload).then((res) => {
      setLoading(false);
      if (res.code === 'OK') {
        SuccessNotification(toast, {
          description: `${Texts?.addToDivisionSuccess} ${divisionToAdd && SITE_KEY_MAP_MO?.[divisionToAdd]}`,
          action: <SuccessIcon className="h-5 w-5" />,
        });
        setAllRowsDataFuncRef?.current(updatedallRowsdata);
        const customerDetailRoute = createUrl(customerId, accountNumber, divisionToAdd || '', '', mailingRecordId);
        router.push(`/${CUSTOMER_DETAILS_PAGE_PATH}/${customerDetailRoute}`);
      } else {
        ErrorNotification(toast, {
          description: `${Texts?.addToDivisionError} ${divisionToAdd && SITE_KEY_MAP_MO?.[divisionToAdd]}`,
          action: <ErrorIcon className="h-5 w-5" />,
        });
      }
    });
  }, []);
  return (
    <>
      {loading && <Loader />}
      <Button className="gbmask p-0 font-medium text-blueLight underline" onClick={() => setShow(true)}>
        {shouldAddCustomerMaster ? 'Prospect' : dialogTriggerText}
      </Button>
      <Dialog onOpenChange={(value) => setShow(value)} open={show}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{Texts?.confirmationHeading}</DialogTitle>
            <DialogDescription>{dialogDescriptionText}</DialogDescription>
          </DialogHeader>
          <div className="flex pt-6">
            <Button className="text-semibold w-36 bg-deepBlue text-white" onClick={handleAddDivision}>
              {Texts?.yesAddUser}
            </Button>
            <Button className="w-36 underline" onClick={() => setShow(false)}>
              {Texts?.cancel}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddUserToDivisionDialog;
